import * as yup from "yup";
import { t } from "i18next";
import {
  stringValidation,
  getStringValidationRequired,
  testAtLeastOneTrue,
  getTrimmedStringValidationRequired,
} from "@/shared/yup";
import type { Person } from "../types";
import { getNoteSchema } from "../../note";

export const personConnectionSchema = yup.object().shape({
  id: yup.string().required(),
  personId: yup.string().required(),
  connectionPersonId: yup.string().required(),
  additionalInformation: stringValidation,
});

const baseEntitySchema = {
  id: yup.string(),
  personId: yup.string().required(),
};

const typeSchema = yup
  .object()
  .shape({
    name: stringValidation,
  })
  .nullable();

const addressSchema = () =>
  yup
    .object()
    .shape({
      ...baseEntitySchema,
      addressType: typeSchema,
      addressStreet1: stringValidation,
      addressStreet2: stringValidation,
      addressCity: stringValidation,
      addressCountry: stringValidation,
      addressPostalCode: stringValidation,
    })
    .test("at-least-one-true", function (value) {
      return testAtLeastOneTrue(this, value, ["addressStreet1", "addressCity", "addressCountry"]);
    });

const phoneSchema = () =>
  yup.object().shape({
    ...baseEntitySchema,
    phoneType: typeSchema,
    number: getStringValidationRequired(),
  });

const emailSchema = () =>
  yup.object().shape({
    ...baseEntitySchema,
    emailType: typeSchema,
    email: yup.string().required(() => t("validation.required")),
  });

const websiteSchema = () =>
  yup.object().shape({
    ...baseEntitySchema,
    websiteType: typeSchema,
    websiteAddress: getStringValidationRequired(),
  });

const socialNetworkSchema = () =>
  yup.object().shape({
    ...baseEntitySchema,
    socialNetworkType: typeSchema,
    socialNetwork: getStringValidationRequired(),
  });

const careerSchema = () =>
  yup
    .object()
    .shape({
      ...baseEntitySchema,
      industry: yup
        .object()
        .shape({
          name: stringValidation,
        })
        .nullable(),
      company: stringValidation,
      about: stringValidation,
      jobTitle: stringValidation,
      careerStart: stringValidation,
      careerEnd: stringValidation,
    })
    .test("at-least-one-true", function (value) {
      return testAtLeastOneTrue(this, value, ["jobTitle", "company"]);
    });

const educationSchema = () =>
  yup
    .object()
    .shape({
      ...baseEntitySchema,
      place: stringValidation,
      major: stringValidation,
      educationStart: stringValidation,
      educationEnd: stringValidation,
      additional: stringValidation,
    })
    .test("at-least-one-true", function (value) {
      return testAtLeastOneTrue(this, value, ["place", "major"]);
    });

const professionalActivitySchema = () =>
  yup
    .object()
    .shape({
      ...baseEntitySchema,
      activity: stringValidation,
      role: stringValidation,
      industry: yup
        .object()
        .shape({
          name: stringValidation,
        })
        .nullable(),
    })
    .test("at-least-one-true", function (value) {
      return testAtLeastOneTrue(this, value, ["role", "activity"]);
    });

const competenceSchema = yup.object().shape({
  id: yup.string(),
  competenceType: yup
    .object()
    .shape({
      name: stringValidation,
    })
    .nullable(),
  about: getStringValidationRequired(),
});

const hobbySchema = yup.object().shape({
  id: yup.string(),
  hobbyType: yup
    .object()
    .shape({
      name: stringValidation,
    })
    .nullable(),
  about: getStringValidationRequired(),
});

const getTagSchema = () =>
  yup.object().shape({
    name: getTrimmedStringValidationRequired(),
  });

const getAnniversarySchema = () =>
  yup.object().shape({
    ...baseEntitySchema,
    anniversaryType: stringValidation,
    description: getStringValidationRequired(),
    anniversary: getStringValidationRequired(),
  });

export const getPersonSchema = () =>
  yup.object().shape({
    id: yup.string(),
    firstName: stringValidation,
    middleName: stringValidation,
    lastName: stringValidation,
    gender: yup.mixed<Person["gender"]>().required().oneOf(["FEMALE", "MALE", "NOT_DEFINED"]),
    title: yup.string(),
    group: yup.string(),
    birthday: stringValidation,
    additionalInformation: stringValidation,
    avatarFile: yup
      .object()
      .shape({
        fileId: getStringValidationRequired(),
        fileName: getStringValidationRequired(),
        type: getStringValidationRequired(),
      })
      .nullable(),
    addresses: yup.array().of(addressSchema()).required(),
    phones: yup.array().of(phoneSchema()).required(),
    emails: yup.array().of(emailSchema()).required(),
    websites: yup.array().of(websiteSchema()).required(),
    socialNetworks: yup.array().of(socialNetworkSchema()).required(),
    careers: yup.array().of(careerSchema()).required(),
    competences: yup.array().of(competenceSchema).required(),
    hobbies: yup.array().of(hobbySchema).required(),
    personConnections: yup.array().of(personConnectionSchema).required(),
    notes: yup.array().of(getNoteSchema()).required(),
    professionalActivities: yup.array().of(professionalActivitySchema()).required(),
    educations: yup.array().of(educationSchema()).required(),
    tags: yup.array().of(getTagSchema()).required(),
    anniversaries: yup.array().of(getAnniversarySchema()).required(),
  });
